import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';

/**
 * Toast — minimal in-app feedback for save-success / save-error events.
 *
 * Wave 1 polish (PR-6). Replaces the silent "panel closes and you guess
 * if it worked" pattern in the admin edit panels. Lives in a fixed container
 * top-right on desktop, top-center on mobile. Auto-dismisses after a few
 * seconds, with manual close on click.
 *
 * Usage:
 *   const { toast } = useToast();
 *   toast.success('Cambios guardados');
 *   toast.error('No se pudo guardar');
 */

export type ToastTone = 'success' | 'error' | 'info';

interface ToastItem {
  id: number;
  tone: ToastTone;
  message: string;
}

interface ToastApi {
  success: (msg: string) => void;
  error: (msg: string) => void;
  info: (msg: string) => void;
}

const ToastContext = createContext<{ toast: ToastApi } | null>(null);

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const nextId = useRef(1);

  const push = useCallback((tone: ToastTone, message: string) => {
    const id = nextId.current++;
    setItems((curr) => [...curr, { id, tone, message }]);
    // Auto-dismiss after 4s.
    setTimeout(() => {
      setItems((curr) => curr.filter((t) => t.id !== id));
    }, 4000);
  }, []);

  const api: ToastApi = {
    success: (m) => push('success', m),
    error: (m) => push('error', m),
    info: (m) => push('info', m),
  };

  return (
    <ToastContext.Provider value={{ toast: api }}>
      {children}
      <ToastContainer items={items} onDismiss={(id) => setItems((c) => c.filter((t) => t.id !== id))} />
    </ToastContext.Provider>
  );
}

export function useToast(): { toast: ToastApi } {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    // Fail soft — if someone forgets the provider, toasts no-op instead of crashing.
    return {
      toast: {
        success: (m) => console.log('[toast/success]', m),
        error: (m) => console.warn('[toast/error]', m),
        info: (m) => console.log('[toast/info]', m),
      },
    };
  }
  return ctx;
}

function ToastContainer({ items, onDismiss }: { items: ToastItem[]; onDismiss: (id: number) => void }) {
  if (items.length === 0) return null;
  return (
    <div
      style={{
        position: 'fixed',
        top: 'calc(env(safe-area-inset-top, 0px) + 16px)',
        right: 16,
        left: 16,
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        zIndex: 10000,
        pointerEvents: 'none',
        alignItems: 'flex-end',
      }}
    >
      {items.map((t) => (
        <ToastRow key={t.id} item={t} onDismiss={() => onDismiss(t.id)} />
      ))}
    </div>
  );
}

function ToastRow({ item, onDismiss }: { item: ToastItem; onDismiss: () => void }) {
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    const h = requestAnimationFrame(() => setMounted(true));
    return () => cancelAnimationFrame(h);
  }, []);

  // Sprint 3.9o: defensive default. If TONE_PALETTE is ever missing the
  // requested tone (stale persisted state, unknown caller, hot-reload
  // mismatch), fall back to 'info' instead of crashing the whole app
  // with 'undefined is not an object (evaluating r.bg)'.
  const palette = TONE_PALETTE[item.tone] ?? TONE_PALETTE.info;

  return (
    <button
      type="button"
      onClick={onDismiss}
      style={{
        pointerEvents: 'auto',
        maxWidth: 420,
        minWidth: 240,
        padding: '12px 16px',
        borderRadius: 12,
        border: `1px solid ${palette.border}`,
        background: palette.bg,
        color: palette.fg,
        fontSize: 14,
        fontWeight: 700,
        fontFamily: 'inherit',
        cursor: 'pointer',
        boxShadow: '0 4px 16px rgba(0,0,0,0.10), 0 1px 3px rgba(0,0,0,0.06)',
        textAlign: 'left',
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        transform: mounted ? 'translateY(0)' : 'translateY(-8px)',
        opacity: mounted ? 1 : 0,
        transition: 'transform .18s ease, opacity .18s ease',
      }}
      aria-live="polite"
    >
      <span aria-hidden style={{ fontSize: 16, fontWeight: 900 }}>{palette.icon}</span>
      <span style={{ flex: 1 }}>{item.message}</span>
    </button>
  );
}

const TONE_PALETTE: Record<ToastTone, { bg: string; fg: string; border: string; icon: string }> = {
  success: { bg: '#E6F0E9', fg: '#1F4A2D', border: '#A6CDB1', icon: '✓' },
  error:   { bg: '#FBE5E5', fg: '#7A1F1F', border: '#D9A0A0', icon: '!' },
  info:    { bg: '#E5EEF8', fg: '#1A3F6B', border: '#9FBADB', icon: 'i' },
};
